import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const CART_JSON_SECTION = 'st-cart-json'; // SCENTREAT: the cart JSON rides along with the add (product-form.js)

/**
 * SCENTREAT bundle tiers v2 (25/09/2026, Wakapaw kit-picker layout). See blocks/st-bundle.liquid.
 * The chosen tier decides how many units go to the cart and which colour each has. Gifts come from the
 * block's data-gifts slots ({id, from, group, label, value, url, img}); slots sharing a group are one card
 * (highest unlocked slot names it, values add up). On submit the product form is intercepted (window
 * capture) and units + every unlocked gift go to the cart in one /cart/add.js request, announced with a
 * CartLinesUpdateEvent like SCENTREAT's product-form.js.
 */
class StBundleComponent extends Component {
  requiredRefs = ['tiers'];
  #variants = [];
  #tiers = [];
  #gifts = [];
  #form = null;
  #priceEl = null;
  static #bound = false;

  connectedCallback() {
    super.connectedCallback();
    const parse = (v) => { try { return JSON.parse(v || '[]'); } catch { return []; } };
    this.#variants = parse(this.dataset.variants);
    this.#tiers = parse(this.dataset.tiers);
    this.#gifts = parse(this.dataset.gifts);
    const section = this.closest('.shopify-section') || document;
    const comp = [...section.querySelectorAll('product-form-component')].find((f) => !f.closest('st-upsell-component') && !f.closest('st-bundle-component'));
    this.#form = comp ? comp.querySelector('form') : null;
    const wanted = String(this.dataset.defaultTier || '2');
    const initial = this.refs.tiers.find((r) => r.value === wanted) || this.refs.tiers[0];
    if (initial) initial.checked = true;
    // one unit: the colour picked in the (hidden) theme picker; a set: one of each colour in order
    const current = this.#form?.querySelector('input[name="id"]')?.value;
    for (const box of this.querySelectorAll('[data-tier-sizes]')) {
      const selects = [...box.querySelectorAll('select')];
      selects.forEach((sel, i) => {
        const open = [...sel.options].filter((o) => !o.disabled);
        if (!open.length) return;
        if (selects.length === 1) { if (current && open.some((o) => o.value === current)) sel.value = current; return; }
        sel.value = open[i % open.length].value;
      });
    }
    this.#render();
    document.addEventListener('change', this.#onAddonToggle);
    if (!StBundleComponent.#bound) {
      StBundleComponent.#bound = true;
      window.addEventListener('submit', StBundleComponent.onSubmit, true);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('change', this.#onAddonToggle);
  }

  #onAddonToggle = (event) => {
    if (event.target?.closest?.('st-upsell-component')) setTimeout(() => this.#syncButton(), 0);
  };

  get tier() {
    const r = this.refs.tiers.find((x) => x.checked);
    return r ? Number(r.value) : 1;
  }

  #tierCfg(n) {
    return this.#tiers.find((t) => Number(t.qty) === n) || { qty: n, pct: 0 };
  }

  #chosen(n) {
    const box = this.querySelector(`[data-tier-sizes="${n}"]`);
    return box ? [...box.querySelectorAll('select')].map((s) => Number(s.value)).filter(Boolean) : [];
  }

  /** {now, was} in cents for tier n: was = the units' compare-at prices */
  #totals(n) {
    const pct = Number(this.#tierCfg(n).pct || 0);
    let now = 0, was = 0;
    for (const id of this.#chosen(n)) {
      const v = this.#variants.find((x) => Number(x.id) === id);
      if (!v) continue;
      // Shopify allocates the percentage per line and drops the fraction of a cent
      now += Number(v.price) - Math.floor(Number(v.price) * pct / 100 + 1e-9);
      was += Number(v.compare || v.price);
    }
    return { now, was };
  }

  /** gift slots unlocked at tier n */
  #unlocked(n) {
    return this.#gifts.filter((g) => Number(g.from || 1) <= n);
  }

  /** one card per group: [{label, value, img, url, locked, from}] */
  #cards(n) {
    const groups = new Map();
    for (const g of this.#gifts) {
      const k = String(g.group);
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(g);
    }
    const out = [];
    for (const list of groups.values()) {
      list.sort((a, b) => Number(a.from) - Number(b.from));
      const open = list.filter((g) => Number(g.from) <= n);
      if (open.length) {
        const top = open[open.length - 1];
        out.push({ label: top.label, value: open.reduce((s, g) => s + Number(g.value || 0), 0), img: top.img, url: top.url, locked: false });
      } else {
        const first = list[0];
        out.push({ label: first.label, value: Number(first.value || 0), img: first.img, url: first.url, locked: true, from: Number(first.from) });
      }
    }
    return out;
  }

  #giftValue(n) {
    return this.#unlocked(n).reduce((s, g) => s + Number(g.value || 0), 0);
  }

  select() { this.#render(); }
  resize() { this.#render(); }

  #render() {
    const active = this.tier;
    const unit = this.dataset.unit || 'unit', units = this.dataset.units || 'units';
    for (const t of [1, 2, 3]) {
      const { now, was } = this.#totals(t);
      const retail = was + this.#giftValue(t);
      const set = (sel, text) => { const el = this.querySelector(sel); if (el) el.textContent = text; };
      const cnt = this.#chosen(t).length || t;
      const priceEl = this.querySelector(`[data-tier-price="${t}"]`);
      if (priceEl) priceEl.innerHTML = cnt > 1 ? `${this.#money(Math.round(now / cnt))}<small> /each</small>` : this.#money(now);
      const wasEl = this.querySelector(`[data-tier-compare="${t}"]`);
      if (wasEl) { wasEl.textContent = was > now ? this.#money(was) : ''; wasEl.hidden = !(was > now); }
      const pct = Number(this.#tierCfg(t).pct || 0);
      set(`[data-tier-pct="${t}"]`, pct > 0 ? `Extra ${pct}% off` : (was > now && now > 0 ? `Save ${Math.round((1 - now / was) * 100)}%` : ''));
      const count = this.#chosen(t).length || t;
      set(`[data-tier-sub="${t}"]`, count === 1 ? `1 ${unit}` : `${count} ${units} · ${this.#money(Math.round(now / count))} each`);
      const details = this.querySelector(`[data-tier-details="${t}"]`);
      if (details) details.hidden = t !== active;
    }
    this.#renderSummary(active);
    this.#renderGifts(active);
    this.#renderSave(active);
    const first = this.#chosen(active)[0];
    const input = this.#form?.querySelector('input[name="id"]');
    if (input && first && String(first) !== input.value) input.value = String(first);
    this.#syncButton();
  }

  #renderSummary(n) {
    const { now, was } = this.#totals(n);
    const count = this.#chosen(n).length || n;
    const unit = this.dataset.unit || 'unit', units = this.dataset.units || 'units';
    const set = (sel, text) => { const el = this.querySelector(sel); if (el) el.textContent = text; };
    set('[data-sum-title]', count === 1 ? `1 ${unit}` : `${count} ${units}`);
    set('[data-sum-each]', count > 1 ? `${this.#money(Math.round(now / count))} each` : '');
    set('[data-sum-now]', this.#money(now));
    const w = this.querySelector('[data-sum-was]'); if (w) { w.textContent = was > now ? this.#money(was) : ''; w.hidden = !(was > now); }
    const b = this.querySelector('[data-sum-n]'); if (b) { b.textContent = `×${count}`; b.hidden = count < 2; }
    for (const step of this.querySelectorAll('[data-step]')) {
      const k = Number(step.dataset.step);
      step.classList.toggle('is-done', k <= n);
      step.classList.toggle('is-next', k === n + 1);
      const i = step.querySelector('i'); if (i) i.textContent = k <= n ? '✓' : String(k);
    }
  }

  #esc(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  #renderGifts(n) {
    const box = this.querySelector('[data-gift-cards]');
    if (!box) return;
    box.innerHTML = this.#cards(n).map((c) => `
      <li class="stb__gift${c.locked ? ' stb__gift--locked' : ''}">
        ${c.img ? `<img class="stb__gift-img" src="${this.#esc(c.img)}" alt="" loading="lazy" width="56" height="56">` : ''}
        <span class="stb__gift-name">${this.#esc(c.label)}</span>
        ${c.locked
          ? `<span class="stb__gift-lock">🔒 Buy ${c.from}</span>`
          : `<span class="stb__gift-val">${c.value > 0 ? `<s>${this.#money(c.value)}</s>` : ''}<b>FREE</b></span>`}
      </li>`).join('');
  }

  #renderSave(n) {
    const { now, was } = this.#totals(n);
    const count = this.#chosen(n).length || n;
    const unit = this.dataset.unit || 'unit', units = this.dataset.units || 'units';
    const rows = [[`${count} ${count === 1 ? unit : units}`, this.#money(was)]];
    for (const c of this.#cards(n)) if (!c.locked) rows.push([`Gift: ${this.#esc(c.label)}`, `<b>${c.value > 0 ? `<s>${this.#money(c.value)}</s>` : ''}FREE</b>`]);
    const retail = was + this.#giftValue(n);
    const list = this.querySelector('[data-save-list]');
    if (list) list.innerHTML = rows.map((r) => `<li><span>${r[0]}</span><span>${r[1]}</span></li>`).join('');
    const set = (sel, text) => { const el = this.querySelector(sel); if (el) el.textContent = text; };
    set('[data-save-retail]', this.#money(retail));
    set('[data-save-pay]', this.#money(now));
    set('[data-save-amt]', this.#money(Math.max(0, retail - now)));
  }

  #syncButton() {
    if (!this.#form) return;
    const button = this.#form.querySelector('.add-to-cart-button, button[name="add"]');
    if (!button) return;
    const text = button.querySelector('.add-to-cart-text') || button;
    if (!this.#priceEl) {
      this.#priceEl = button.querySelector('.st-atc-price');
      if (!this.#priceEl) { this.#priceEl = document.createElement('span'); this.#priceEl.className = 'st-atc-price'; text.appendChild(this.#priceEl); }
    }
    const n = this.tier;
    let cents = this.#totals(n).now;
    const section = this.closest('.shopify-section') || document;
    for (const box of section.querySelectorAll('st-upsell-component')) {
      if (!box.checked || !box.dataset.variantId || box.classList.contains('st-upsell--gift')) continue;
      try {
        const vid = this.#form.querySelector('input[name="id"]')?.value;
        const combined = JSON.parse(box.dataset.priceCombined || '{}')[vid];
        const base = JSON.parse(box.dataset.priceBase || '{}')[vid];
        if (combined && base) cents += StBundleComponent.toCents(combined) - StBundleComponent.toCents(base);
      } catch {}
    }
    const walker = document.createTreeWalker(text, NodeFilter.SHOW_TEXT, { acceptNode: (node) => node.textContent.trim() && !node.parentElement.closest('.st-atc-price, .svg-wrapper') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT });
    const labelNode = walker.nextNode();
    if (labelNode) labelNode.textContent = n > 1 ? `${this.dataset.atcLabel || 'Add to cart'} (${n}) ` : `${this.dataset.atcLabel || 'Add to cart'} `;
    this.#priceEl.textContent = ` - ${this.#money(cents)} →`;
  }

  #money(cents) {
    const fmt = this.dataset.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const withComma = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, withComma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toLocaleString('en-US'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
      .replace(/<[^>]+>/g, '');
  }

  static toCents(money) {
    const n = String(money).replace(/[^\d.,]/g, '').replace(/,(?=\d{3}\b)/g, '').replace(',', '.');
    return Math.round(parseFloat(n) * 100) || 0;
  }

  /** cart items: every chosen unit, every unlocked gift, ticked paid add-on boxes */
  cartItems() {
    const n = this.tier;
    const counts = new Map();
    for (const id of this.#chosen(n)) counts.set(id, (counts.get(id) || 0) + 1);
    const items = [...counts].map(([id, quantity]) => ({ id, quantity }));
    // 29/09/2026: gifts are child lines of the Pillar (Shopify nested cart lines): removing it removes them too.
    // parent_id may only name a variant sent in this same request - the first Pillar line.
    const parent = items[0]?.id ? { parent_id: items[0].id } : {};
    for (const g of this.#unlocked(n)) items.push({ id: Number(g.id), quantity: 1, ...parent });
    const section = this.closest('.shopify-section') || document;
    for (const box of section.querySelectorAll('st-upsell-component')) {
      if (box.checked && box.dataset.variantId && !box.classList.contains('st-upsell--gift')) items.push({ id: Number(box.dataset.variantId), quantity: 1 });
    }
    return items;
  }

  static onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;
    if (form.dataset.stSkipBatch === '1') return;
    const section = form.closest('.shopify-section') || document;
    const bundle = section.querySelector('st-bundle-component');
    if (!bundle) return;
    const items = bundle.cartItems();
    if (!items.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const comp = form.closest('product-form-component') || form;
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
    const button = form.querySelector('button[name="add"]');
    if (button) button.setAttribute('aria-busy', 'true');
    const itemCount = items.reduce((s, i) => s + i.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();

    // SCENTREAT: send the request first, then announce it (the drawer opens and fills while it travels)
    const request = fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: [...sectionIds, CART_JSON_SECTION].join(',') }),
    });
    comp.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );

    const cartFromSections = (sections) => {
      const html = sections?.[CART_JSON_SECTION];
      if (typeof html !== 'string') return null;
      try {
        const m = html.match(/<script[^>]*data-st-cart[^>]*>([\s\S]*?)<\/script>/);
        const cart = m ? JSON.parse(m[1]) : null;
        if (!cart || !Array.isArray(cart.items)) return null;
        const cookie = document.cookie.split('; ').find((c) => c.startsWith('cart='));
        if (!cookie) return null;
        cart.token = decodeURIComponent(cookie.slice(5));
        return cart;
      } catch {
        return null;
      }
    };
    const fetchCart = () => fetch(Theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const base = { source: 'product-form-component', sourceId: String(comp.id || ''), itemCount, productId: comp.dataset?.productId };

    request
      .then((r) => r.json())
      .then(async (data) => {
        if (data.status) {
          console.warn('[st-bundle] add rejected:', data.description || data.message);
          comp.dispatchEvent(new CartErrorEvent({ error: data.message || 'Add to cart failed', code: 'INVALID', detail: { description: data.description, errors: data.errors } }));
          const cart = await fetchCart();
          deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { ...base, items: cart.items, didError: true } });
          return;
        }
        const cart = cartFromSections(data.sections) ?? (await fetchCart());
        const { [CART_JSON_SECTION]: _json, ...drawer } = data.sections || {};
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { ...base, items: cart.items, sections: drawer, didError: false } });
      })
      .catch((error) => {
        console.error('[st-bundle]', error);
        deferred.reject(error);
        comp.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Add to cart failed', code: 'SERVICE_UNAVAILABLE' }));
      })
      .finally(() => {
        if (button) button.removeAttribute('aria-busy');
      });
  };
}

if (!customElements.get('st-bundle-component')) customElements.define('st-bundle-component', StBundleComponent);
